import { useNavigate } from "react-router-dom";
import helpStyle from "../styles/help.module.scss";

const HELP_SECTIONS = [
  {
    id: "section-1",
    title: "Navigating the App",
    paragraphs: [
      "MATLIB is designed to help you browse, compare, and save material references quickly. Use the main library view to scan available materials, apply filters, and open a material card for more detailed images, specifications, and availability information.",
      "When you open a material, focus on the details panel first. That view is where you can review finish, composition, color direction, imagery, and any supporting notes that help you decide whether the material is relevant to your project.",
      "If you are returning to the library after viewing an item, your best workflow is to keep filtering as you go. Narrowing by the most important properties first usually makes the library easier to scan than trying to review everything at once.",
    ],
  },
  {
    id: "section-2",
    title: "Building and Managing a Collection",
    paragraphs: [
      "Use the collection tools to save materials you want to revisit. A collection acts like a shortlist, making it easier to compare options across multiple vendors or categories without losing your place in the main library.",
      "The collection page supports both quick review and editing. Use Array view for visual scanning, then switch to List edit view when you need to reorder items or remove swatches before sharing.",
      "Changes to collection order are intentional and should be treated as presentation order for export workflows. Keep the highest-priority materials near the top if the output will be reviewed by a team.",
    ],
  },
  {
    id: "section-3",
    title: "Exporting a Collection to PDF",
    paragraphs: [
      "Use the dedicated Collection PDF page to prepare exports. Set a collection title, optional description, and decide whether pricing should be included before generating the document.",
      "In Array view, each swatch card can include multiple image options. Use the image selector dots to choose which image is sent to the PDF for each material.",
      "The page keeps a short session history of recently created PDFs so you can reopen previous exports quickly while working. This history is session-based and is not intended as long-term storage.",
    ],
  },
  {
    id: "section-4",
    title: "Sharing and Reviewing Materials",
    paragraphs: [
      "When sharing materials internally, use the material detail view and collection tools to gather the clearest images and most relevant specifications first. This reduces back-and-forth and helps reviewers understand why each option was selected.",
      "If your workflow includes exporting or presenting materials externally, confirm that product names, imagery, and notes are current before sharing. MATLIB should support review and selection, but final purchasing or production decisions should still be checked against the most recent vendor information.",
    ],
  },
  {
    id: "section-5",
    title: "Account Access and Support",
    paragraphs: [
      "Access to MATLIB may differ by user type. Some users may have a direct login, while others may access the library through an approved identifier or vendor-specific workflow. If your access stops working, contact the MATLIB administrator or your internal point of contact.",
      "Use the Contact page when you need platform support, sourcing guidance, or help with collection workflows. Include a clear subject and enough context in your message so the team can respond faster.",
      "If you are building a collection for a team discussion, make sure the materials you keep in that list are the ones you would realistically present. A tighter collection usually makes decision-making faster.",
      "For the best experience, use current browser versions and avoid having multiple sessions open in different tabs when editing or reviewing materials. If something looks outdated, refresh the page and reopen the material before assuming the data is current.",
    ],
  },
];

const PRIVACY_SECTIONS = [
  {
    title: "Our commitment",
    paragraphs: [
      "MATLIB does not keep your personal information, and we do not use it for anything. Access details exist only so you can open the library. That is the entire purpose.",
      "We are not building profiles, tracking you for marketing, selling data, or analyzing your activity for commercial purposes. If you can sign in, it is only to let you browse materials — nothing more.",
    ],
  },
  {
    title: "What login is for",
    paragraphs: [
      "Login, identifiers, and brand access are a gate to the material library. They are not a reason for us to collect, retain, or reuse personal details beyond what is required to let authorized people in.",
      "Saved collections and similar in-app tools are there to help you work inside MATLIB. They are not used by us as a source of personal data for any other product, campaign, or third-party use.",
    ],
  },
  {
    title: "About the information in MATLIB",
    paragraphs: [
      "Everything you see in MATLIB is generic information for reference only. Images, names, notes, and specs are here to help you look at materials — not as a substitute for a live quote, lab sheet, or production confirmation.",
      "Need something more specific? Reach out to Jesse Smith at jsmith@asiatan.com.",
      "MATLIB is a toy we give customers so they can interact with material images. Have fun with it. Please don’t get mad at the tool.",
    ],
  },
];

const HELP_NAV = [
  { id: "section-1", label: "Navigation" },
  { id: "section-2", label: "Collections" },
  { id: "section-3", label: "PDF Export" },
  { id: "section-4", label: "Sharing" },
  { id: "section-5", label: "Support" },
  { id: "privacy", label: "Privacy" },
];

function Help() {
  const navigate = useNavigate();

  const handleClickScroll = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className={`content ${helpStyle.helpPage}`}>
      <div className={helpStyle.helpBackdrop} aria-hidden="true"></div>

      <div className={helpStyle.helpLayout}>
        <aside className={helpStyle.helpSidebar}>
          <div className={helpStyle.sidebarCard}>
            <p className={helpStyle.sidebarEyebrow}>Support</p>
            <h1 className={helpStyle.sidebarTitle}>MATLIB Help</h1>
            <p className={helpStyle.sidebarCopy}>
              Guidance for browsing materials and managing collections — plus a
              clear privacy stance: we do not keep or use your personal
              information.
            </p>

            <div className={helpStyle.sidebarNav}>
              {HELP_NAV.map((item) => (
                <button
                  key={item.id}
                  className={helpStyle.navButton}
                  onClick={() => handleClickScroll(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              className={helpStyle.backButton}
              onClick={() => navigate("/library")}
            >
              Back to the Library
            </button>
          </div>
        </aside>

        <main className={helpStyle.helpBody}>
          <section className={helpStyle.heroPanel}>
            <div>
              <p className={helpStyle.sectionEyebrow}>Quick Start</p>
              <h2 className={helpStyle.heroTitle}>
                Clean guidance for using MATLIB effectively.
              </h2>
              <p className={helpStyle.heroCopy}>
                Start with navigation, build a focused collection, then read the
                privacy section. The short version: MATLIB is only a way to
                access the library. We do not keep user information or do
                anything with it.
              </p>
            </div>

            <div className={helpStyle.heroStats}>
              <div className={helpStyle.statCard}>
                <span>5</span>
                <p>Core help sections</p>
              </div>
              <div className={helpStyle.statCard}>
                <span>1</span>
                <p>Privacy overview</p>
              </div>
            </div>
          </section>

          {HELP_SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className={helpStyle.helpSection}
            >
              <p className={helpStyle.sectionEyebrow}>Help Section</p>
              <h2 className={helpStyle.sectionTitle}>{section.title}</h2>
              <div className={helpStyle.sectionDivider}></div>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className={helpStyle.sectionParagraph}>
                  {paragraph}
                </p>
              ))}
            </section>
          ))}

          <div id="privacy" className={helpStyle.anchorOffset}></div>
          <div className={helpStyle.privacyHeader}>
            <p className={helpStyle.sectionEyebrow}>Policy</p>
            <h1 className={helpStyle.privacyTitle}>Privacy Policy</h1>
          </div>
          <section className={helpStyle.privacyCallout} aria-label="Privacy priority">
            <p className={helpStyle.privacyCalloutEyebrow}>Priority message</p>
            <h2 className={helpStyle.privacyCalloutTitle}>
              We do not keep your information. We do not use it.
            </h2>
            <p className={helpStyle.privacyCalloutCopy}>
              MATLIB exists so authorized people can access the materials
              library. Login is only a door into that library. Your details are
              not harvested, profiled, marketed against, or reused by us for any
              other purpose.
            </p>
          </section>
          {PRIVACY_SECTIONS.map((section) => (
            <section key={section.title} className={helpStyle.helpSection}>
              <h2 className={helpStyle.sectionTitle}>{section.title}</h2>
              <div className={helpStyle.sectionDivider}></div>
              {section.paragraphs.map((paragraph) => {
                const email = "jsmith@asiatan.com";
                const emailIndex = paragraph.indexOf(email);
                return (
                  <p key={paragraph} className={helpStyle.sectionParagraph}>
                    {emailIndex === -1 ? (
                      paragraph
                    ) : (
                      <>
                        {paragraph.slice(0, emailIndex)}
                        <a
                          className={helpStyle.sectionLink}
                          href={`mailto:${email}`}
                        >
                          {email}
                        </a>
                        {paragraph.slice(emailIndex + email.length)}
                      </>
                    )}
                  </p>
                );
              })}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}

export default Help;
